import { vesselCardStyles } from "../constants/vessel-card-styles.constants";
import { VesselCardDashboardHeaderProps } from "../models/vessel-card-header.models";
import VesselCardPreferenceButton from "./VesselCardPreferenceButton";
import VesselCardVesselSelector from "./VesselCardVesselSelector";
import { InformationCircleIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";

// Keeps vessel switching available while the card loads or fails.
// Opens search when no vessel is selected, including when navigation returns to the base card route.
// Search closes after a vessel is selected or focus leaves the control.
const VesselCardDashboardHeader = ({
  vessel,
  selectedImo,
  useMockData,
  vessels,
  vesselQuery,
  vesselStatus,
  vesselError,
  hasMoreVessels,
  isLoadingMoreVessels,
  onOpenHelp,
  onQueryChange,
  onLoadMoreVessels,
  onSelectVessel,
  onSearchOpenChange,
}: VesselCardDashboardHeaderProps) => {
  const [isSearchOpen, setIsSearchOpen] = useState(!selectedImo);
  const searchButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!selectedImo) {
      setIsSearchOpen(true);
      onSearchOpenChange?.(true);
    }
  }, [selectedImo, onSearchOpenChange]);

  const setSearchOpen = (isOpen: boolean) => {
    setIsSearchOpen(isOpen);
    onSearchOpenChange?.(isOpen);
  };

  return (
    <header className="mb-6 flex flex-wrap items-center justify-between gap-4" data-test="vessel-card-header">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-4">
        <div className="min-w-0 flex-1">
          <div
            className="relative flex items-center gap-2"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                setSearchOpen(false);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.stopPropagation();
                setSearchOpen(false);
                searchButtonRef.current?.focus();
              }
            }}
          >
            <h1 className="truncate text-2xl font-semibold leading-8 text-gray-900 dark:text-gray-100">
              {vessel?.vesselName ?? "Vessel Card"}
            </h1>
            <button
              ref={searchButtonRef}
              type="button"
              aria-label="Select another vessel"
              aria-expanded={isSearchOpen}
              aria-controls="vessel-card-header-search"
              data-test="vessel-card-header-search-button"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-green hover:bg-primary-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-green dark:text-teal-200 dark:hover:bg-teal-950/40"
              onClick={() => setSearchOpen(!isSearchOpen)}
            >
              <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            {isSearchOpen && (
              <div
                id="vessel-card-header-search"
                className="absolute left-0 top-full z-40 w-[min(24rem,calc(100vw-4rem))] rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800"
                data-test="vessel-card-header-search"
              >
                <VesselCardVesselSelector
                  autoFocus
                  error={vesselError}
                  hasMore={hasMoreVessels}
                  isLoadingMore={isLoadingMoreVessels}
                  onLoadMore={onLoadMoreVessels}
                  onQueryChange={onQueryChange}
                  onSelectVessel={(imo) => {
                    onSelectVessel(imo);
                    setSearchOpen(false);
                    searchButtonRef.current?.focus();
                  }}
                  options={vessels}
                  query={vesselQuery}
                  selectedVessel={vessel}
                  status={vesselStatus}
                />
              </div>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Vessel card{selectedImo && ` · IMO ${selectedImo}`}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {vessel && !useMockData && <VesselCardPreferenceButton key={vessel.imo} vessel={vessel} />}
        <button
          type="button"
          data-test="vessel-card-help-button"
          onClick={onOpenHelp}
          disabled={!vessel}
          className={vesselCardStyles.secondaryButton}
        >
          <InformationCircleIcon className="h-5 w-5 text-green" aria-hidden="true" />
          Help / FAQ
        </button>
      </div>
    </header>
  );
};

export default VesselCardDashboardHeader;
